import React from 'react';

import styles from './index.module.less';

// 进度节点接口定义
interface ProgressNode {
  id: string;
  type: 'small' | 'medium' | 'large'; // 对应SVG中不同大小的节点
  isActive: boolean; // 是否为激活状态（亮色）
  position?: { x: number; y: number }; // 可选的精确位置
}

// 进度组接口定义
interface ProgressGroup {
  id: string;
  nodes: ProgressNode[];
}

// 组件属性接口
interface ControlDecorationsProps {
  groups?: ProgressGroup[];
  className?: string;
}

const ControlDecorations: React.FC<ControlDecorationsProps> = ({ groups = [], className }) => {
  // 根据传入的 groups 动态计算进度状态，如果没有传入则使用默认静态数据
  const processedGroups = React.useMemo(() => {
    if (groups.length > 0) {
      return groups;
    }

    // 默认的静态数据（用于展示和调试）
    return [
      {
        id: 'default-group',
        nodes: [
          {
            id: 'node-1',
            type: 'large' as const,
            isActive: true,
            position: { x: 7.42969, y: 7.51123 },
          },
          {
            id: 'node-2',
            type: 'medium' as const,
            isActive: true,
            position: { x: 13.4316, y: 101.316 },
          },
          {
            id: 'node-3',
            type: 'medium' as const,
            isActive: false,
            position: { x: 13.4316, y: 215.794 },
          },
          {
            id: 'node-4',
            type: 'large' as const,
            isActive: false,
            position: { x: 7.42969, y: 312.869 },
          },
          {
            id: 'node-5',
            type: 'medium' as const,
            isActive: false,
            position: { x: 13.4316, y: 409.528 },
          },
          {
            id: 'node-6',
            type: 'medium' as const,
            isActive: false,
            position: { x: 13.4316, y: 531.716 },
          },
        ],
      },
    ];
  }, [groups]);

  const activeGroups = processedGroups;

  // 动态渲染连接线，根据节点位置和激活状态显示
  const renderConnections = () => {
    if (activeGroups.length === 0) return null;

    const nodes = activeGroups[0].nodes;
    if (nodes.length < 2) return null;

    const connections: Array<{
      x: number;
      y: number;
      width: number;
      height: number;
      fill: string;
      isActive: boolean;
      segmentIndex: number; // 添加线段索引，用于判断是否应该高亮
      connectionIndex: number; // 添加连接索引，标识这是哪两个节点之间的连接
    }> = [];

    // 渐变色配置
    const activeColors = ['#B2FFEC', '#60F3CE', '#60AEF3', '#3073D9', '#6466E3', '#35367A'];
    const inactiveColor = '#323945';

    // 找到当前激活的 medium 节点
    const activeMediumNodes = nodes.filter((node) => node.type === 'medium' && node.isActive);

    // 生成节点之间的连接线段
    for (let i = 0; i < nodes.length - 1; i++) {
      const currentNode = nodes[i];
      const nextNode = nodes[i + 1];

      if (!currentNode.position || !nextNode.position) continue;

      const startY = currentNode.position.y + (currentNode.type === 'large' ? 30 : 15);
      const endY = nextNode.position.y;
      const distance = endY - startY;

      // 原有的连接段激活逻辑
      const isSegmentActive = currentNode.isActive && nextNode.isActive;

      if (distance > 10) {
        // 计算连接线段的数量和尺寸
        const segmentCount = Math.max(3, Math.min(12, Math.floor(distance / 8)));
        const segmentHeight = distance / segmentCount;

        for (let j = 0; j < segmentCount; j++) {
          const progress = j / (segmentCount - 1);
          const segmentY = startY + j * segmentHeight;

          // 创建大小渐变效果：中间最大，两端较小
          const sizeProgress = 1 - Math.abs(progress - 0.5) * 2;
          const minWidth = 1.7;
          const maxWidth = 5.1;
          const segmentWidth = minWidth + (maxWidth - minWidth) * sizeProgress;

          // 计算x位置，保持居中
          const baseX = 18.5;
          const segmentX = baseX - segmentWidth / 2;

          // 新的高亮逻辑：当有 medium 节点被选中时，高亮该节点上下各一半的连接线段
          let shouldHighlight = isSegmentActive;

          if (activeMediumNodes.length > 0) {
            for (const activeMedium of activeMediumNodes) {
              if (!activeMedium.position) continue;

              // 计算当前线段与激活的 medium 节点的距离
              const mediumY = activeMedium.position.y + 5; // medium 节点中心位置
              const segmentCenterY = segmentY + segmentHeight / 2;

              // 计算总的连接线段数量，然后取上下各一半
              const totalSegmentsInConnection = segmentCount;
              const halfSegments = Math.floor(totalSegmentsInConnection / 2);

              // 判断当前线段是否在 medium 节点的影响范围内
              // 影响范围：以 medium 节点为中心，上下各取一半的线段数量，并增加超出阈值
              const segmentFromMedium = Math.abs(segmentCenterY - mediumY);
              const avgSegmentHeight = distance / segmentCount;
              const baseInfluenceRange = halfSegments * avgSegmentHeight;

              // 增加超出阈值：额外增加2个线段的高度作为缓冲区
              const overflowThreshold = avgSegmentHeight * 2;
              const influenceRange = baseInfluenceRange + overflowThreshold;

              if (segmentFromMedium <= influenceRange) {
                shouldHighlight = true;
                break;
              }
            }
          }

          connections.push({
            x: segmentX,
            y: segmentY,
            width: segmentWidth,
            height: Math.min(segmentHeight * 0.8, segmentWidth),
            fill: shouldHighlight ? activeColors[j % activeColors.length] : inactiveColor,
            isActive: shouldHighlight,
            segmentIndex: j,
            connectionIndex: i,
          });
        }
      }
    }

    // 为最后一个 medium 节点后面补充一半的 small 节点
    const lastNode = nodes[nodes.length - 1];
    if (lastNode && lastNode.type === 'medium' && lastNode.position) {
      const startY = lastNode.position.y + 15; // medium 节点底部
      const additionalDistance = 60; // 额外的距离，用于放置补充的 small 节点

      // 计算补充线段的数量（取一半）
      const supplementSegmentCount = Math.max(2, Math.floor(6)); // 固定6个线段的一半
      const segmentHeight = additionalDistance / supplementSegmentCount;

      for (let j = 0; j < supplementSegmentCount; j++) {
        const progress = j / (supplementSegmentCount - 1);
        const segmentY = startY + j * segmentHeight;

        // 创建大小渐变效果
        const sizeProgress = 1 - progress * 0.8; // 从大到小渐变
        const minWidth = 1.7;
        const maxWidth = 5.1;
        const segmentWidth = minWidth + (maxWidth - minWidth) * sizeProgress;

        // 计算x位置，保持居中
        const baseX = 18.5;
        const segmentX = baseX - segmentWidth / 2;

        // 判断是否应该高亮（当最后一个 medium 节点被选中时）
        let shouldHighlight = false;
        if (activeMediumNodes.length > 0) {
          for (const activeMedium of activeMediumNodes) {
            if (!activeMedium.position) continue;

            // 如果激活的是最后一个 medium 节点，则高亮这些补充的线段
            if (activeMedium === lastNode) {
              shouldHighlight = true;
              break;
            }
          }
        }

        connections.push({
          x: segmentX,
          y: segmentY,
          width: segmentWidth,
          height: Math.min(segmentHeight * 0.8, segmentWidth),
          fill: shouldHighlight ? activeColors[j % activeColors.length] : inactiveColor,
          isActive: shouldHighlight,
          segmentIndex: j,
          connectionIndex: nodes.length - 1, // 标记为最后一个连接
        });
      }
    }

    return connections.map((conn, index) => (
      <rect
        key={`connection-${index}`}
        x={conn.x}
        y={conn.y}
        width={conn.width}
        height={conn.height}
        fill={conn.fill}
        rx={conn.width * 0.2} // 添加圆角
      />
    ));
  };

  // 渲染单个进度节点
  const renderProgressNode = (node: ProgressNode, groupIndex: number, nodeIndex: number) => {
    const xPos = node.position?.x || 0;
    const yPos = node.position?.y || 0;
    const nodeId = `node-${node.id}-${groupIndex}-${nodeIndex}`;

    if (node.type === 'large') {
      return (
        <g key={nodeId} filter={`url(#filter_${nodeId})`}>
          <g clipPath={`url(#clip_${nodeId})`}>
            {/* 基础矩形 */}
            <rect
              x={xPos}
              y={yPos}
              width="22.0029"
              height="22.0029"
              rx="2.52493"
              fill={node.isActive ? '#96F5D8' : 'url(#paint_linear_inactive_base)'}
              fillOpacity={node.isActive ? '0.88' : '0.72'}
            />

            {node.isActive ? (
              // 激活状态的样式层
              <>
                <rect
                  x={xPos}
                  y={yPos}
                  width="22.0029"
                  height="22.0029"
                  rx="2.52493"
                  fill="url(#paint_radial_universal)"
                  fillOpacity="0.7"
                />
                <rect
                  x={xPos}
                  y={yPos}
                  width="22.0029"
                  height="22.0029"
                  rx="2.52493"
                  fill="url(#paint_linear_universal)"
                  fillOpacity="0.69"
                />
                {/* 激活状态的三角形装饰 */}
                <g filter={`url(#filter_triangle_bottom_${nodeId})`}>
                  <path
                    d={`M${xPos + 0.0717} ${yPos + 22.1341}L${xPos + 11.0001} ${yPos + 11.0454}L${xPos + 21.9565} ${yPos + 22.1341}H${xPos + 0.0717}Z`}
                    fill="url(#paint_triangle_universal)"
                    fillOpacity="0.67"
                  />
                </g>
                <g opacity="0.73" filter={`url(#filter_triangle_top_${nodeId})`}>
                  <path
                    d={`M${xPos + 21.9859} ${yPos - 0.0212}L${xPos + 11.0005} ${yPos + 11.0455}L${xPos + 0.0429688} ${yPos - 0.0212}H${xPos + 21.9859}Z`}
                    fill="url(#paint_triangle_universal)"
                    fillOpacity="0.87"
                  />
                </g>
              </>
            ) : (
              // 非激活状态的样式层 - 基于用户提供的SVG样式
              <>
                <rect
                  x={xPos}
                  y={yPos}
                  width="22.0029"
                  height="22.0029"
                  rx="2.52493"
                  fill="url(#paint_radial_inactive)"
                  fillOpacity="0.46"
                />
                <rect
                  x={xPos}
                  y={yPos}
                  width="22.0029"
                  height="22.0029"
                  rx="2.52493"
                  fill="url(#paint_linear_inactive_overlay)"
                  fillOpacity="0.44"
                />
                {/* 非激活状态的三角形装饰 - 保留渐变但置灰 */}
                <g opacity="0.5" filter={`url(#filter_triangle_bottom_${nodeId})`}>
                  <path
                    d={`M${xPos + 0.0717} ${yPos + 22.1341}L${xPos + 11.0001} ${yPos + 11.0454}L${xPos + 21.9565} ${yPos + 22.1341}H${xPos + 0.0717}Z`}
                    fill="url(#paint_triangle_inactive_bottom)"
                    fillOpacity="0.4"
                  />
                </g>
                <g opacity="0.5" filter={`url(#filter_triangle_top_${nodeId})`}>
                  <path
                    d={`M${xPos + 21.9859} ${yPos - 0.0212}L${xPos + 11.0005} ${yPos + 11.0455}L${xPos + 0.0429688} ${yPos - 0.0212}H${xPos + 21.9859}Z`}
                    fill="url(#paint_triangle_inactive_top1)"
                    fillOpacity="0.62"
                  />
                  <path
                    d={`M${xPos + 21.9859} ${yPos - 0.0212}L${xPos + 11.0005} ${yPos + 11.0455}L${xPos + 0.0429688} ${yPos - 0.0212}H${xPos + 21.9859}Z`}
                    fill="url(#paint_triangle_inactive_top2)"
                    fillOpacity="0.59"
                  />
                </g>
              </>
            )}
          </g>
        </g>
      );
    }

    if (node.type === 'medium') {
      return (
        <g key={nodeId} opacity="0.72" filter={`url(#filter_${nodeId})`}>
          <g clipPath={`url(#clip_${nodeId})`}>
            {/* 基础矩形 */}
            <rect
              x={xPos}
              y={yPos}
              width="10"
              height="10"
              rx="1.52"
              fill={node.isActive ? '#96F5D8' : 'url(#paint_linear_inactive_base)'}
              fillOpacity={node.isActive ? '0.88' : '0.72'}
            />

            {node.isActive ? (
              // 激活状态的样式层
              <>
                <rect
                  x={xPos}
                  y={yPos}
                  width="10"
                  height="10"
                  rx="1.52"
                  fill="url(#paint_radial_universal)"
                  fillOpacity="0.7"
                />
                <rect
                  x={xPos}
                  y={yPos}
                  width="10"
                  height="10"
                  rx="1.52"
                  fill="url(#paint_linear_universal)"
                  fillOpacity="0.69"
                />
                {/* 激活状态的三角形装饰 */}
                <g filter={`url(#filter_triangle_bottom_${nodeId})`}>
                  <path
                    d={`M${xPos - 0.0328} ${yPos + 10.0588}L${xPos + 4.99345} ${yPos + 5.02}L${xPos + 10.0175} ${yPos + 10.0588}H${xPos - 0.0328}Z`}
                    fill="url(#paint_triangle_universal)"
                    fillOpacity="0.4"
                  />
                </g>
                <g opacity="0.73" filter={`url(#filter_triangle_top_${nodeId})`}>
                  <path
                    d={`M${xPos + 10.0311} ${yPos - 0.0198}L${xPos + 4.99389} ${yPos + 5.02}L${xPos - 0.0188} ${yPos - 0.0198}H${xPos + 10.0311}Z`}
                    fill="url(#paint_triangle_universal)"
                    fillOpacity="0.62"
                  />
                </g>
              </>
            ) : (
              // 非激活状态的样式层 - 与large节点相同的逻辑
              <>
                <rect
                  x={xPos}
                  y={yPos}
                  width="10"
                  height="10"
                  rx="1.52"
                  fill="url(#paint_radial_inactive)"
                  fillOpacity="0.46"
                />
                <rect
                  x={xPos}
                  y={yPos}
                  width="10"
                  height="10"
                  rx="1.52"
                  fill="url(#paint_linear_inactive_overlay)"
                  fillOpacity="0.44"
                />
                {/* 非激活状态的三角形装饰 - 保留渐变但置灰 */}
                <g opacity="0.5" filter={`url(#filter_triangle_bottom_${nodeId})`}>
                  <path
                    d={`M${xPos - 0.0328} ${yPos + 10.0588}L${xPos + 4.99345} ${yPos + 5.02}L${xPos + 10.0175} ${yPos + 10.0588}H${xPos - 0.0328}Z`}
                    fill="url(#paint_triangle_inactive_bottom)"
                    fillOpacity="0.4"
                  />
                </g>
                <g opacity="0.5" filter={`url(#filter_triangle_top_${nodeId})`}>
                  <path
                    d={`M${xPos + 10.0311} ${yPos - 0.0198}L${xPos + 4.99389} ${yPos + 5.02}L${xPos - 0.0188} ${yPos - 0.0198}H${xPos + 10.0311}Z`}
                    fill="url(#paint_triangle_inactive_top1)"
                    fillOpacity="0.62"
                  />
                  <path
                    d={`M${xPos + 10.0311} ${yPos - 0.0198}L${xPos + 4.99389} ${yPos + 5.02}L${xPos - 0.0188} ${yPos - 0.0198}H${xPos + 10.0311}Z`}
                    fill="url(#paint_triangle_inactive_top2)"
                    fillOpacity="0.59"
                  />
                </g>
              </>
            )}
          </g>
        </g>
      );
    }

    if (node.type === 'small') {
      return (
        <g key={nodeId} opacity="0.72" filter={`url(#filter_${nodeId})`}>
          <g clipPath={`url(#clip_${nodeId})`}>
            <rect
              x={xPos}
              y={yPos}
              width="10"
              height="10"
              rx="1.14754"
              fill={node.isActive ? '#96F5D8' : '#323945'}
              fillOpacity={node.isActive ? '0.88' : '0.72'}
            />
            {node.isActive && (
              <>
                <rect
                  x={xPos}
                  y={yPos}
                  width="10"
                  height="10"
                  rx="1.14754"
                  fill="url(#paint_radial_universal)"
                  fillOpacity="0.46"
                />
                <rect
                  x={xPos}
                  y={yPos}
                  width="10"
                  height="10"
                  rx="1.14754"
                  fill="url(#paint_linear_universal)"
                  fillOpacity="0.44"
                />
                <g filter={`url(#filter_triangle_bottom_${nodeId})`}>
                  <path
                    d={`M${xPos - 0.0328} ${yPos + 10.0588}L${xPos + 4.99345} ${yPos + 5.02}L${xPos + 10.0175} ${yPos + 10.0588}H${xPos - 0.0328}Z`}
                    fill="url(#paint_triangle_universal)"
                    fillOpacity="0.4"
                  />
                </g>
                <g opacity="0.73" filter={`url(#filter_triangle_top_${nodeId})`}>
                  <path
                    d={`M${xPos + 10.0311} ${yPos - 0.0198}L${xPos + 4.99389} ${yPos + 5.02}L${xPos - 0.0188} ${yPos - 0.0198}H${xPos + 10.0311}Z`}
                    fill="url(#paint_triangle_universal)"
                    fillOpacity="0.62"
                  />
                </g>
              </>
            )}
          </g>
        </g>
      );
    }

    return null;
  };

  // 固定SVG高度，基于默认布局
  const svgHeight = 566;

  return (
    <div className={`${styles.decorationContainer} ${className || ''}`}>
      <svg
        width="37"
        height={svgHeight}
        viewBox={`0 0 37 ${svgHeight}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* 定义渐变和滤镜（基于原始SVG） */}
        <defs>
          {/* 三角形渐变 - 通用版本 */}
          <linearGradient
            id="paint_triangle_universal"
            x1="30%"
            y1="60%"
            x2="80%"
            y2="90%"
            gradientUnits="objectBoundingBox"
          >
            <stop stopColor="#D9D9D9" />
            <stop offset="1" stopColor="#D9D9D9" stopOpacity="0" />
          </linearGradient>

          {/* 激活状态的渐变定义 - 使用 objectBoundingBox 确保一致性 */}
          <radialGradient
            id="paint_radial_universal"
            cx="50%"
            cy="50%"
            r="70%"
            gradientUnits="objectBoundingBox"
          >
            <stop stopColor="#00F2FF" />
            <stop offset="1" stopColor="#00F2FF" stopOpacity="0" />
          </radialGradient>

          <linearGradient
            id="paint_linear_universal"
            x1="70%"
            y1="90%"
            x2="130%"
            y2="10%"
            gradientUnits="objectBoundingBox"
          >
            <stop stopColor="#3E54FF" stopOpacity="0" />
            <stop offset="1" stopColor="#3E54FF" />
          </linearGradient>

          {/* 非激活状态的渐变定义 - 基于用户提供的SVG样式 */}
          <linearGradient
            id="paint_linear_inactive_base"
            x1="50%"
            y1="0%"
            x2="50%"
            y2="100%"
            gradientUnits="objectBoundingBox"
          >
            <stop stopColor="#216265" />
            <stop offset="1" stopColor="#0F3031" />
          </linearGradient>

          <radialGradient
            id="paint_radial_inactive"
            cx="0"
            cy="0"
            r="1"
            gradientUnits="objectBoundingBox"
            gradientTransform="translate(42.4% 76.7%) rotate(65.556) scale(54.1% 65.8%)"
          >
            <stop stopColor="#D0DEDF" />
            <stop offset="1" stopColor="#D0DEDF" stopOpacity="0" />
          </radialGradient>

          <linearGradient
            id="paint_linear_inactive_overlay"
            x1="50%"
            y1="60.8%"
            x2="122.2%"
            y2="4.8%"
            gradientUnits="objectBoundingBox"
          >
            <stop stopColor="#99CED3" stopOpacity="0" />
            <stop offset="1" stopColor="#99CED3" />
          </linearGradient>

          {/* 非激活状态三角形渐变 - 置灰版本 */}
          <linearGradient
            id="paint_triangle_inactive_bottom"
            x1="46.3%"
            y1="66.3%"
            x2="131.5%"
            y2="95.2%"
            gradientUnits="objectBoundingBox"
          >
            <stop stopColor="#D9D9D9" />
            <stop offset="1" stopColor="#D9D9D9" stopOpacity="0" />
          </linearGradient>

          <linearGradient
            id="paint_triangle_inactive_top1"
            x1="50.3%"
            y1="45.7%"
            x2="7.3%"
            y2="55.1%"
            gradientUnits="objectBoundingBox"
          >
            <stop stopColor="#D9D9D9" stopOpacity="0" />
            <stop offset="1" stopColor="#D9D9D9" />
          </linearGradient>

          <linearGradient
            id="paint_triangle_inactive_top2"
            x1="50.3%"
            y1="38.8%"
            x2="139.5%"
            y2="54.9%"
            gradientUnits="objectBoundingBox"
          >
            <stop stopColor="#D9D9D9" stopOpacity="0" />
            <stop offset="1" stopColor="#D9D9D9" />
          </linearGradient>

          {/* 滤镜效果 */}
          {activeGroups.map((group) =>
            group.nodes.map((node, nodeIndex) => {
              const nodeId = `node-${node.id}-0-${nodeIndex}`;
              return (
                <React.Fragment key={`filters-${nodeId}`}>
                  {/* 主要滤镜 */}
                  <filter
                    id={`filter_${nodeId}`}
                    x={node.position?.x}
                    y={node.position?.y}
                    width={node.type === 'large' ? '22.0029' : '10'}
                    height={node.type === 'large' ? '22.0029' : '10'}
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                  >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feBlend
                      mode="normal"
                      in="SourceGraphic"
                      in2="BackgroundImageFix"
                      result="shape"
                    />
                    <feColorMatrix
                      in="SourceAlpha"
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                      result="hardAlpha"
                    />
                    <feOffset />
                    <feGaussianBlur stdDeviation="0.48695" />
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                    <feColorMatrix
                      type="matrix"
                      values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.65 0"
                    />
                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow" />
                  </filter>

                  {/* 三角形滤镜 */}
                  <filter
                    id={`filter_triangle_bottom_${nodeId}`}
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                  >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feBlend
                      mode="normal"
                      in="SourceGraphic"
                      in2="BackgroundImageFix"
                      result="shape"
                    />
                    <feGaussianBlur
                      stdDeviation={node.type === 'large' ? '0.721407' : '0.327869'}
                      result="effect1_foregroundBlur"
                    />
                  </filter>

                  <filter
                    id={`filter_triangle_top_${nodeId}`}
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                  >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feBlend
                      mode="normal"
                      in="SourceGraphic"
                      in2="BackgroundImageFix"
                      result="shape"
                    />
                    <feGaussianBlur
                      stdDeviation={node.type === 'large' ? '0.766495' : '0.348361'}
                      result="effect1_foregroundBlur"
                    />
                  </filter>

                  {/* 裁剪路径 */}
                  <clipPath id={`clip_${nodeId}`}>
                    {node.type === 'large' ? (
                      <rect
                        x={node.position?.x}
                        y={node.position?.y}
                        width="22.0029"
                        height="22.0029"
                        rx="2.52493"
                        fill="white"
                      />
                    ) : node.type === 'medium' ? (
                      <rect
                        x={node.position?.x}
                        y={node.position?.y}
                        width="10"
                        height="10"
                        rx="1.52"
                        fill="white"
                      />
                    ) : (
                      <rect
                        x={node.position?.x}
                        y={node.position?.y}
                        width="10"
                        height="10"
                        rx="1.14754"
                        fill="white"
                      />
                    )}
                  </clipPath>
                </React.Fragment>
              );
            }),
          )}
        </defs>

        {/* 渲染连接线 */}
        {renderConnections()}

        {/* 渲染所有进度组 */}
        {activeGroups.map((group, groupIndex) => (
          <g key={group.id}>
            {/* 渲染节点 */}
            {group.nodes.map((node, nodeIndex) => renderProgressNode(node, groupIndex, nodeIndex))}
          </g>
        ))}
      </svg>
    </div>
  );
};

export default ControlDecorations;
